import { useState } from 'react'
import { type Product } from '@/features/shop/services/Product/types'
import { Button, TextButton } from '@shipt/design-system-buttons'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { Column, Row } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { getDimensionValues } from '@/features/shop/services/Product/utils/variations'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'

const MODAL_MAX_VALUES = 10

type Props = {
  variationProducts: Product[]
  handleSelectProperty: (name: string, value: string, isAvailable: boolean) => void
  selectedProperties: Record<string, string>
  isModal?: boolean
  productPageUrl?: string
  onSeeAllOptions?: () => void
}

export const ProductDetailVariation = ({
  variationProducts,
  handleSelectProperty,
  selectedProperties,
  isModal = false,
  productPageUrl,
  onSeeAllOptions,
}: Props) => {
  const [initialSelectedProperties] = useState(() => (isModal ? selectedProperties : undefined))
  const [expandedDimensions, setExpandedDimensions] = useState<Record<string, boolean>>({})
  const toggleExpanded = (name: string) => {
    setExpandedDimensions((prev) => ({ ...prev, [name]: !prev[name] }))
  }
  const dimensions = getDimensionValues(
    variationProducts,
    selectedProperties,
    initialSelectedProperties,
  )
  useExperiment(ExperimentType.VARIATIONS_ENHANCED_PROPERTIES, {
    autoTrack: true,
  })

  return (
    <Column spacing="xl" padding={['lg', 0]}>
      {dimensions.map(({ name, values, hasAllSwatches }) => {
        const isTruncated = isModal && values.length > MODAL_MAX_VALUES
        const isExpanded = !!expandedDimensions[name]
        const visibleValues =
          isTruncated && !isExpanded ? values.slice(0, MODAL_MAX_VALUES) : values

        return (
          <Column spacing="sm" key={name}>
            <Column spacing="xs">
              <Headline size="sm">Select {name}</Headline>
              {!!hasAllSwatches && (
                <Body size="sm" variant="secondary">
                  {name}: {selectedProperties[name]}
                </Body>
              )}
            </Column>
            <Row spacing="md" wrap>
              <RadioGroup role="radiogroup" aria-label={name}>
                {visibleValues.map(({ value, isAvailable, swatchUrl }) =>
                  hasAllSwatches ? (
                    <OptionSwatchButton
                      key={value}
                      role="radio"
                      aria-checked={selectedProperties[name] === value}
                      aria-label={`${value}${!isAvailable ? ' - unavailable with current selected properties' : ''}`}
                      data-is-selected={selectedProperties[name] === value}
                      data-is-unavailable={!isAvailable}
                      onClick={() => handleSelectProperty(name, value, isAvailable)}
                    >
                      <SwatchImage src={swatchUrl!} alt="" width={40} height={40} />
                    </OptionSwatchButton>
                  ) : (
                    <OptionTextButton
                      key={value}
                      role="radio"
                      aria-checked={selectedProperties[name] === value}
                      aria-label={`${value}${!isAvailable ? ' - unavailable with current selected properties' : ''}`}
                      size="sm"
                      variant="secondary"
                      onClick={() => handleSelectProperty(name, value, isAvailable)}
                      data-is-selected={selectedProperties[name] === value}
                      data-is-unavailable={!isAvailable}
                    >
                      {value}
                    </OptionTextButton>
                  ),
                )}
              </RadioGroup>
              {!!isTruncated &&
                (productPageUrl ? (
                  <PDPButtonLink href={productPageUrl} onClick={onSeeAllOptions} showUnderline>
                    See all options
                  </PDPButtonLink>
                ) : (
                  <TextButton size="md" onClick={() => toggleExpanded(name)}>
                    {isExpanded ? 'See less' : `See ${values.length - MODAL_MAX_VALUES} more`}
                  </TextButton>
                ))}
            </Row>
          </Column>
        )
      })}
    </Column>
  )
}

const RadioGroup = styled.div`
  /*
   * Do not remove this - it allows us to accessibly label the dimension options
   * as a radiogroup while allowing them to be in the same flex layout as the
   * PDPButtonLink
   */
  display: contents;
`

const PDPButtonLink = styled(TextButtonLink)`
  margin-left: 1rem;
`

const OptionTextButton = styled(Button)<{
  'data-is-selected': boolean
  'data-is-unavailable': boolean
}>`
  &[data-is-selected='true'] {
    background-color: ${({ theme }) => theme.button.color.background.default.subtle_hover};
  }

  &[data-is-unavailable='true'] {
    border-style: dashed;
  }
`
const OptionSwatchButton = styled.button<{
  'data-is-selected': boolean
  'data-is-unavailable': boolean
}>`
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.gray300};
  background-color: transparent;

  &[data-is-selected='true'],
  &:hover {
    border-color: ${({ theme }) => theme.button.color.border.default.bold_hover};
  }

  &[data-is-unavailable='true'] {
    border-style: dashed;
  }
`
const SwatchImage = styled(Image)`
  border-radius: 50%;
  display: block;
`
